"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldAlert, CircleCheck as CheckCircle2, Flame, ChevronRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function FireHazmat() {
  const shouldReduceMotion = useReducedMotion();
  const keyPoints = ["Instant identification of unknown liquids, solids & powders", "Non-contact sampling through sealed containers", "Tailored calibration for Irish and UK emergency response protocols"];
  return <section id="herofirehazmat" data-nav="light" className="relative overflow-hidden bg-background py-28 md:py-36">
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: 7 cols */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
                <li className="flex items-center gap-1.5">
                  <Link to="/HomePage" className="transition-colors hover:text-foreground">
                    Home
                  </Link>
                  <ChevronRight className="size-3.5 text-muted-foreground/60" aria-hidden="true" />
                </li>
                <li className="flex items-center gap-1.5">
                  <Link to="/IndustriesPage" className="transition-colors hover:text-foreground">
                    Industries
                  </Link>
                  <ChevronRight className="size-3.5 text-muted-foreground/60" aria-hidden="true" />
                </li>
                <li className="flex items-center">
                  <span className="font-medium text-foreground" aria-current="page">
                    Fire &amp; HazMat
                  </span>
                </li>
              </ol>
            </nav>

            <div className="mb-6 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                <ShieldAlert className="mr-1.5 size-3.5 text-primary" />
                Emergency Services & First Response
              </Badge>
              <Badge variant="outline" className="border-border text-muted-foreground">
                <Flame className="mr-1 size-3 text-primary" />
                HazMat Certified
              </Badge>
            </div>

            <h1 className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Rapid On-Scene Identification for Fire &amp; HazMat Response
            </h1>

            <p className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl">
              Immediate chemical characterization and threat detection in the hot zone. Delivering ruggedized Raman and FTIR field screening tools to Irish and UK emergency responders navigating unknown spills, toxic industrial chemicals, and hazardous materials.
            </p>

            <ul className="mt-6 space-y-2.5">
              {keyPoints.map((point, index) => <li key={index} data-index={index} className="flex items-center text-sm font-medium text-foreground">
                  <CheckCircle2 className="mr-2.5 size-4 shrink-0 text-primary" />
                  <span>{point}</span>
                </li>)}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-md font-semibold shadow-sm">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Your Application
                  <ArrowRight className="ml-1.5 size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md border-border bg-background text-foreground hover:bg-secondary font-semibold">
                <Link to="/SafetyAndSecurityPage">
                  Explore Safety &amp; Security Solutions
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: 5 cols */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 28
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
              <div className="overflow-hidden rounded-2xl border border-border bg-card p-2 shadow-xl">
                <div className="relative aspect-4/3 w-full overflow-hidden rounded-xl bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hazmat_technician_field_analysis.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hazmat_technician_field_analysis.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hazmat_technician_field_analysis.png-wvc-sizes" alt="HazMat technician performing on-site chemical identification with handheld analytical device" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent pointer-events-none" />
                </div>

                <div className="p-4 sm:p-5">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground uppercase tracking-wide">
                      Field Screening Deployment
                    </span>
                    <span className="rounded-full bg-secondary px-2.5 py-0.5 font-mono text-secondary-foreground">
                      {"MIL-STD-810G"}
                    </span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                    Ruggedized handheld systems enabling decisive threat identification within seconds under harsh field conditions.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}